import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon, BloomMark } from "../components/Icon.jsx";

// Landing page after a successful hosted-checkout payment. The backend callback has
// already verified + confirmed the order(s) before redirecting here, so this page just
// confirms to the customer and clears the cart (only when the checkout came from the
// cart — a direct "Buy Now" never sets the marker, so an unrelated cart is preserved).
export default function OrderSuccess({ setCart, clearCart, removePurchasedItems }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get("order") || "";

  useEffect(() => {
    // Capture the markers' INTENT once, then remove them immediately so they can't linger and
    // wrongly whole-clear a LATER order. Deps are stable ([]) so re-renders can't reset the timers.
    const wholeClear = localStorage.getItem("bloom_pending_clear_cart") === "1"; // cart checkout
    let buyNow = null; // "Buy Now": remove ONLY the bought item, keep unrelated cart items
    try { const r = localStorage.getItem("bloom_purchased_items"); if (r) buyNow = JSON.parse(r); } catch { /* ignore */ }
    try { localStorage.removeItem("bloom_pending_clear_cart"); localStorage.removeItem("bloom_purchased_items"); } catch { /* ignore */ }

    // Apply the clear REPEATEDLY over ~2s. The logged-in customer object loads asynchronously
    // here, and the identity key-swap can re-hydrate the cart AFTER a single clear — the "paid
    // but still in cart" bug for logged-in users. Re-applying (using the captured intent, not
    // localStorage) beats that async settle; clearCart / removePurchasedItems are idempotent and
    // clearCart also sets the anti-resurrection guard the key-swap now respects.
    const apply = () => {
      try {
        localStorage.removeItem("bloom_abandon_ids"); // payment done → keep this checkout's hold
        if (wholeClear) (clearCart ? clearCart() : setCart?.([]));
        if (Array.isArray(buyNow) && buyNow.length) removePurchasedItems?.(buyNow);
      } catch { /* ignore */ }
    };
    apply();
    const t1 = setTimeout(apply, 600);
    const t2 = setTimeout(apply, 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--offwhite)", padding: "40px 16px", fontFamily: "'Syne',sans-serif" }}>
      <div className="card" style={{ width: "100%", maxWidth: 440, padding: 40, textAlign: "center", background: "var(--white)", borderRadius: 4, boxShadow: "0 10px 44px rgba(57,57,63,0.12)" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(6,118,71,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px", fontSize: 36 }}><Icon n="success" size={36} inline /></div>
        <h1 style={{ fontSize: 26, margin: "0 0 8px", color: "var(--green)" }}>Payment received!</h1>
        <p style={{ fontSize: 14, color: "var(--ink-soft,rgba(57,57,63,0.6))", lineHeight: 1.6, margin: "0 0 6px" }}>
          Your order is confirmed. The store has been notified and a confirmation has been emailed to you. <BloomMark />
        </p>
        {orderId && <p style={{ fontSize: 12, fontFamily: "monospace", color: "var(--ink-soft,rgba(57,57,63,0.55))", margin: "0 0 26px" }}>Order {orderId}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button className="btn-pink" style={{ padding: 14, borderRadius: 4, fontSize: 14 }} onClick={() => navigate(orderId ? `/customer/orders?order=${encodeURIComponent(orderId)}` : "/customer/orders")}>Track my order →</button>
          <button onClick={() => navigate("/")} style={{ padding: 13, borderRadius: 4, fontSize: 14, fontWeight: 700, background: "transparent", border: "1.5px solid var(--border-mid,rgba(57,57,63,0.18))", color: "var(--ink,#39393F)", cursor: "pointer" }}>Continue shopping</button>
        </div>
      </div>
    </div>
  );
}
